/**
 * Canonical assessment Task classification helpers.
 *
 * Shared by medplum-server bots and medplum-ui so one-time assignments and Take Now Tasks are distinguished the same
 * way. Recurring (standing-order) and deferred assessments are no longer supported (ENG-3764).
 */
import type { CodeableConcept, Extension, Questionnaire, Task } from '@medplum/fhirtypes';

import {
  ASSESSMENT_CANCELLATION_REASON_EXT,
  CLIENT_ASSESSMENT_ASSIGNMENT_CODE,
  CLIENT_TAKE_NOW_ASSESSMENT_CODE,
  TEMPLATE_CATEGORY_SYSTEM,
  TASK_TYPE_SYSTEM,
} from '../constants';

export type AssessmentTaskKind = 'one-time' | 'take-now' | 'none';

function hasTaskCode(task: Task, code: string): boolean {
  return (task.code?.coding ?? []).some((c) => c.system === TASK_TYPE_SYSTEM && c.code === code);
}

export function getAssessmentCancellationReason(task: Task): CodeableConcept | undefined {
  return (
    task.statusReason ??
    task.extension?.find((e) => e.url === ASSESSMENT_CANCELLATION_REASON_EXT)?.valueCodeableConcept
  );
}

/** Client assessment assignment (`client-assessment-assignment`). */
export function isAssessmentAssignmentTask(task: Task): boolean {
  return hasTaskCode(task, CLIENT_ASSESSMENT_ASSIGNMENT_CODE);
}

/** Provider-administered Take Now (`client-take-now-assessment`). */
export function isTakeNowAssessmentTask(task: Task): boolean {
  return hasTaskCode(task, CLIENT_TAKE_NOW_ASSESSMENT_CODE);
}

/** Assessments do not receive Encounter or ChargeItem (ENG-3553). */
export function isBillableAssessmentAssignment(_task: Task): boolean {
  return false;
}

export function classifyAssessmentTask(task: Task): AssessmentTaskKind {
  if (isTakeNowAssessmentTask(task)) return 'take-now';
  if (isAssessmentAssignmentTask(task)) return 'one-time';
  return 'none';
}

export function isMeasuresQuestionnaire(q: Questionnaire): boolean {
  const tag = q.meta?.tag?.find((t) => t.system === TEMPLATE_CATEGORY_SYSTEM);
  return tag?.code === 'measures' || tag?.code === 'scored-measures';
}

export function getExt(resource: { extension?: Extension[] }, url: string): Extension | undefined {
  return resource.extension?.find((e) => e.url === url);
}
